import { defineStore } from "pinia";
import { createBaseStoreRefactor } from "./baseStoreRefactor";
import { useDayReportsStoreRefactor } from "./dayReportsStoreRefactor";

/**
 * Day Report Search Store
 *
 * This store is specifically designed for search functionality and is separate from
 * the main day reports store. It provides:
 *
 * 1. Search-only context (no caching, infinite loading support)
 * 2. Day report-specific getters for search results
 * 3. Real-time updates bridge from main store
 *
 * REAL-TIME UPDATES ARCHITECTURE:
 * ================================
 *
 * Problem: Search stores don't have ActionCable subscriptions (to avoid conflicts),
 * but users expect to see new items appear in search results when they're created
 * via ActionCable in other parts of the app.
 *
 * Solution: Bridge pattern that connects main store ActionCable updates to search stores:
 *
 * 1. When search() is called, we subscribe to the main store's _upsertItem method
 * 2. We override the main store's _upsertItem to also add new items to search results
 * 3. ALL new items are added regardless of search criteria (simplified UX)
 * 4. This ensures users always see new items appear in search views
 *
 * Flow:
 * ActionCable → Main Store → _upsertItem override → Search Store (if matches criteria)
 *
 * Benefits:
 * - No ActionCable conflicts (only main stores subscribe)
 * - Search stores get real-time updates for all new items
 * - Clean separation of concerns
 * - Simplified UX - users always see new items appear
 */

// Create base store configuration for day reports with infinite loading
const dayReportsBaseStore = createBaseStoreRefactor({
  endpoint: "/api/v1/day_reports",
  name: "DayReportSearchStoreRefactor",
  objectName: "Veldwerkverslag", // Auto-generates CRUD success messages
  enableCache: false, // Day reports change frequently, don't cache
  actionCableChannel: null, // Disable ActionCable for search store (main store handles real-time updates)
  infiniteLoading: true, // Enable infinite loading for day reports
});

export const useDayReportSearchStore = defineStore("dayReportSearch", {
  ...dayReportsBaseStore,

  state: () => {
    const baseState = dayReportsBaseStore.state();
    return {
      ...baseState,
      currentSearchParams: null, // Track current search parameters
      mainStoreSubscribed: false, // Track if we're listening to main store
    };
  },

  getters: {
    ...dayReportsBaseStore.getters,

    // Convenience aliases for search context
    searchResults: (state) => state.data,
    hasSearchResults: (state) => state.data.length > 0,

    // Day report-specific getters
    submittedSearchResults: (state) =>
      (state.data || []).filter((dayReport) => dayReport.submitted === true),
    unsubmittedSearchResults: (state) =>
      (state.data || []).filter((dayReport) => dayReport.submitted === false),

    // Get search results by user
    getSearchResultsByUser: (state) => (userId) => {
      return (state.data || []).filter(
        (dayReport) => dayReport.user_id == userId,
      );
    },

    // Get search results by date range
    getSearchResultsByDateRange: (state) => (startDate, endDate) => {
      return (state.data || []).filter((dayReport) => {
        const reportDate = new Date(dayReport.date);
        return (
          reportDate >= new Date(startDate) && reportDate <= new Date(endDate)
        );
      });
    },
  },

  actions: {
    ...dayReportsBaseStore.actions,

    // Override search to track search parameters and use correct endpoint
    async search(searchParams = {}, append = false) {
      // Store current search parameters for real-time filtering
      this.currentSearchParams = searchParams.q || searchParams;

      // Subscribe to main store updates if not already subscribed
      if (!this.mainStoreSubscribed) {
        this.subscribeToMainStore();
      }

      // Use the search endpoint specifically for day reports
      const endpoint = "/api/v1/day_reports/search";

      // Call the base search method with the correct endpoint
      return await dayReportsBaseStore.actions.search.call(
        this,
        searchParams,
        append,
        false,
        endpoint,
      );
    },

    // Subscribe to main store for real-time updates
    subscribeToMainStore() {
      if (this.mainStoreSubscribed) return;

      const mainStore = useDayReportsStoreRefactor();
      const searchStore = this; // Capture the search store context

      // Override the main store's _upsertItem to also update search store
      const originalUpsertItem = mainStore._upsertItem;
      mainStore._upsertItem = (item, prepend = false) => {
        // Call original method
        const result = originalUpsertItem.call(mainStore, item, prepend);

        // Always add new items to search store (simplified UX - users always see new items)
        if (searchStore.currentSearchParams) {
          // Check if item is already in search results
          const searchData = Array.isArray(searchStore.data)
            ? searchStore.data
            : [];
          const existsInSearch = searchData.some(
            (searchItem) => searchItem.id === item.id,
          );
          if (!existsInSearch) {
            console.log(
              "DayReportSearchStore: Adding new item from main store:",
              item.id,
            );
            searchStore._upsertItem(item, true); // Prepend new items
          }
        }

        return result;
      };

      this.mainStoreSubscribed = true;
      console.log("DayReportSearchStore: Subscribed to main store updates");
    },

    // Override clear to reset search parameters
    clear() {
      this.currentSearchParams = null;
      return dayReportsBaseStore.actions.clear.call(this);
    },
  },
});
